import { ref } from "vue";
import { getRoundCount } from "@lib/utils/formatAmountForStripe";
import { toast } from "@/ui-plus/sonner";
import type { AddressType } from "@lib/zod/userAddressSchema";
import { generateUUID } from '@lib/utils/generateUUID';
import { zmgPublicCacheClient } from "@api-client/zmg_cache";
import type { DeliveryteamOrderInfoType } from "@lib/zod/cartItemState";
import { type storeOrdersApiClient } from '@api-client/store_orders';
import type { ResType } from '@api-client/typeUtils';
type OrderDetailsType = ResType<typeof storeOrdersApiClient[":storeId"]['get_by_order_ids']['$post']>["data"][number];

export type UndeliverableAction = 'leave_at_door' | 'return' | 'discard';
export type ManifestItem = { name: string; quantity: number };
export type deliveryOptionsType = ResType<typeof zmgPublicCacheClient.store.delivery_options[':id']['data.json']['$get']>['data'];
export type uberDirectSettingsType = ResType<typeof zmgPublicCacheClient.store.uber_direct_settings[':id']['data.json']['$get']>['data'];
export type addressType = ResType<typeof zmgPublicCacheClient.store.address[':id']['data.json']['$get']>['data'];
export type baseType = ResType<typeof zmgPublicCacheClient.store.base[':id']['data.json']['$get']>['data'];

const cachedStoreData = ref<
    Record<
        string, { // storeId
            storeId: string;
            address: addressType;
            base: baseType;
            deliveryOptions: deliveryOptionsType;
            uberDirectSettings: uberDirectSettingsType;
        }>
>({});

export const courierRequestState_perOrderRef = ref<
    Record<
        string, { // storeOrderId
            pickupAddress: AddressType | null;
            deliveryAddress: AddressType | null;
            tipCourier: number;
            manifestItems: ManifestItem[];
            dropoffSellerNotes: string;
            returnNotes: string;
            undeliverableAction: UndeliverableAction;
            isUberDirectEnabled: boolean;
            deliveryteamOrderInfosType?: DeliveryteamOrderInfoType;
            cachedStoreData?: {
                storeId: string;
                address: addressType;
                base: baseType;
                deliveryOptions: deliveryOptionsType;
                uberDirectSettings: uberDirectSettingsType;
            };
        }>
>({});

export function initState(orderDetails: OrderDetailsType) {
    if (!courierRequestState_perOrderRef.value[orderDetails.storeOrderId]) {
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId] = {
            pickupAddress: null,
            deliveryAddress: null,
            tipCourier: 0,
            manifestItems: [],
            dropoffSellerNotes: '',
            returnNotes: '',
            undeliverableAction: 'return',
            isUberDirectEnabled: false,
        }
        loadOrderState(orderDetails);// no await, just load the state
    }
}

async function loadOrderState(orderDetails: OrderDetailsType) {
    try {
        if (!cachedStoreData.value[orderDetails.storeId]) {
            const [
                addressRes,
                baseRes,
                deliveryOptionsRes,
                uberDirectSettingsRes
            ] = await Promise.all([
                zmgPublicCacheClient.store.address[':id']['data.json'].$get({
                    param: { id: orderDetails.storeId },
                }).then(r => r.json()),
                zmgPublicCacheClient.store.base[':id']['data.json'].$get({
                    param: { id: orderDetails.storeId },
                }).then(r => r.json()),
                zmgPublicCacheClient.store.delivery_options[':id']['data.json'].$get({
                    param: { id: orderDetails.storeId },
                }).then(r => r.json()),
                zmgPublicCacheClient.store.uber_direct_settings[':id']['data.json'].$get({
                    param: { id: orderDetails.storeId },
                }).then(r => r.json()),
            ]);
            cachedStoreData.value[orderDetails.storeId] = {
                storeId: orderDetails.storeId,
                address: addressRes.data,
                base: baseRes.data,
                deliveryOptions: deliveryOptionsRes.data,
                uberDirectSettings: uberDirectSettingsRes.data,
            };
        }
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].cachedStoreData = cachedStoreData.value[orderDetails.storeId];
        const addr = cachedStoreData.value[orderDetails.storeId].address;
        const base = cachedStoreData.value[orderDetails.storeId].base;
        const deliveryOptions = cachedStoreData.value[orderDetails.storeId].deliveryOptions;
        const uberDirectSettings = cachedStoreData.value[orderDetails.storeId].uberDirectSettings;
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].dropoffSellerNotes = deliveryOptions?.dropoff_seller_notes ?? '';
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].returnNotes = deliveryOptions?.return_notes ?? '';
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].undeliverableAction = deliveryOptions?.undeliverable_action ?? 'return';
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].isUberDirectEnabled = uberDirectSettings?.isUberDirectEnabled ?? false;
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].pickupAddress = {
            addressId: generateUUID(),
            shippingName: orderDetails.storeName || base?.name || '',
            country: addr?.country || '',
            state: addr?.state || '',
            phoneNumber: base?.defaultPhoneNumber || '',
            address1: addr?.address1 || orderDetails.storeAddress1 || '',
            address2: addr?.address2 || '',
            city: addr?.city || '',
            postalCode: addr?.postalCode || '',
            lat: addr?.lat ?? orderDetails.storeLocationLat,
            lng: addr?.lng ?? orderDetails.storeLocationLng,
            instructionsForPickupDelivery: deliveryOptions?.pickup_notes || '',
        };
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].deliveryAddress = orderDetails.deliveryAddressJSON
        // ? { ...orderDetails.deliveryAddressJSON }
        // : null
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].manifestItems = (orderDetails.orderedItemsJSON ?? []).map((i) => ({
            name: i.name,
            quantity: i.quantity,
        }));
        const roundCount = getRoundCount(orderDetails.taxSettingsJSON?.currency ?? 'USD');
        courierRequestState_perOrderRef.value[orderDetails.storeOrderId].tipCourier = Number(
            (orderDetails.subtotalJSON?.calculatedCustomServiceFees?.find(fee => fee.id === 'tip-courier')?.totalWithTax ?? 0).toFixed(roundCount)
        );
        // window emit ready event with orderId
        (window as any).dispatchEvent(new CustomEvent(`${orderDetails.storeOrderId}-courierRequestStateReady`, { detail: orderDetails.storeOrderId }));
    } catch (e) {
        console.error(e);
        toast.error('Failed to load courier request state');
        // } finally {
    }
}